import './series3/polyfills';
// Global stylesheets first, so every app SCSS module that follows wins ties against them. normalize.css
// is what Mantine 6's `withNormalizeCSS` injected; Mantine 7+ ships only a minimal baseline. It comes
// after Mantine's so its `button, input { line-height: 1.15 }` keeps winning for plain elements, as it
// did in v6 (Mantine 8's baseline sets `font: inherit` on them; Mantine components style theirs by class).
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import 'normalize.css';
import { ApolloProvider } from '@apollo/client';
import { createBrowserHistory } from 'history';
import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider as ReduxProvider } from 'react-redux';
import { Router } from 'react-router-dom';
import FatalErrorMessage from './series3/components/FatalErrorMessage/FatalErrorMessage';
import client from './series3/lib/fi-api/client';
import reduxStore from './series3/lib/reduxStore';
import { unregister as unregisterServiceWorker } from './series3/registerServiceWorker';
import '@barkinglabs/promotions-react/dist/index.css';
import Series3App from './series3/Series3App';
import * as Sentry from '@sentry/react';
import { BrowserOptions as SentryBrowserOptions } from '@sentry/browser';
import { HttpClient } from '@sentry/integrations';
import { getTenancy } from './series3/hooks/useTenancy';
import { getDomainConfig } from './series3/lib/domainConfig';
import { ThemeProvider, THEME_ID, createTheme } from '@mui/material/styles';
import './i18n';

/**
 * Create history for React browser router. This history object will be shared between Sentry RUM and the Router
 * component. https://docs.sentry.io/platforms/javascript/guides/react/features/react-router/#react-router-v4v5
 */
const history = createBrowserHistory();

/**
 * Sentry.io configuration
 */
function getSentryConfig(): SentryBrowserOptions {
  const baseConfig: SentryBrowserOptions = {
    dsn: 'https://941c0176eac6d78488430189ded5a5c2@o4506027582619648.ingest.sentry.io/4506061077676032',
    environment: process.env.REACT_APP_ENVIRONMENT,
    // ties each event to the deploy that shipped it, so Sentry can flag an issue that regresses in a release
    release: process.env.REACT_APP_VERSION !== 'unknown' ? process.env.REACT_APP_VERSION : undefined,
    // Errors thrown inside OneTrust's own cookie-banner SDK (cdn.cookielaw.org), which we can't fix from here.
    denyUrls: [/cdn\.cookielaw\.org/],
    // Disable Sentry for test tenancy
    enabled: getTenancy() !== 'test',
    // Thrown by scripts the Facebook and Instagram in-app browsers inject into every page, not by our code, which never
    // touches a native bridge. Shoppers are unaffected.
    ignoreErrors: [
      // iOS autofill script.
      /_AutofillCallbackHandler/,
      // iOS bridge script, run where the native bridge it expects is absent.
      /window\.webkit\.messageHandlers/,
      // Android bridge (iabjs:// scripts), called after the app destroyed it.
      /Java object is gone/,
    ],
  };

  // RUM = Real User Monitoring
  if (process.env.REACT_APP_RUM_ENABLE === 'true') {
    return {
      ...baseConfig,
      integrations: [
        // Creates events for failed client side HTTP requests
        new HttpClient(),
        // Performance Monitoring
        new Sentry.BrowserTracing({
          routingInstrumentation: Sentry.reactRouterV5Instrumentation(history),
        }),
        // Session Replay
        new Sentry.Replay({
          blockAllMedia: false, // default is true
          maskAllText: false, // default is true
        }),
      ],
      // Session Replay
      replaysSessionSampleRate: 0.01,
      replaysOnErrorSampleRate: 0.1, // TODO: bump this up to 100% after we fix the high frequency errors
      // Performance Monitoring
      tracePropagationTargets: ['localhost', /^https:\/\/api.tryfi.com/],
      tracesSampleRate: 0.01,
    };
  }

  return baseConfig;
}

Sentry.init(getSentryConfig());
Sentry.setTag('tenancy', getTenancy());
Sentry.setTag('domain', getDomainConfig().baseDomain);

/**
 * An error reaching this boundary has replaced the whole page with FatalErrorMessage. It's reported as fatal and tagged
 * so alert rules can tell a dead page from the handled errors logInternalError reports at the default level.
 */
const beforeCapture: Sentry.ErrorBoundaryProps['beforeCapture'] = (scope) => {
  scope.setLevel('fatal');
  scope.setTag('crash', 'app_root');
  scope.setTag('tenancy', getTenancy());
  scope.setTag('domain', getDomainConfig().baseDomain);
};

const materialTheme = createTheme();

const root = createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <Sentry.ErrorBoundary fallback={<FatalErrorMessage />} beforeCapture={beforeCapture}>
    <Router history={history}>
      <ApolloProvider client={client}>
        <ReduxProvider store={reduxStore}>
          <ThemeProvider theme={{ [THEME_ID]: materialTheme }}>
            <React.Suspense fallback={null}>
              <Series3App />
            </React.Suspense>
          </ThemeProvider>
        </ReduxProvider>
      </ApolloProvider>
    </Router>
  </Sentry.ErrorBoundary>,
);

unregisterServiceWorker();
